import { Injectable } from '@angular/core';
import { ApiHelper } from '@core/api/api.helper';
import { ApiService } from '@core/api/api.service';
import { UserPreferenceKeysEnum, UserRoleEnum } from '@shared/enums';
import { CurrentUser, UserGroup, UserPreferencesPayload } from '@shared/interfaces';
import { MenuListDTO } from '@shared/interfaces/get-menu-list.interface';
import { HttpResponse } from './http-response.interface';
import { HttpStatusEnum } from './http-status.enum';

@Injectable({ providedIn: 'root' })
export class UserApi {
    private readonly resourceUrl: string = 'users';
    private readonly groupsResourceUrl: string = 'groups';

    constructor(private apiService: ApiService) {}

    async getUserAndPreferences(): Promise<HttpResponse<{ user: CurrentUser; preferences: any }>> {
        const userResponse = await new Promise<any>(resolve =>
            this.apiService.get(`${this.resourceUrl}/current-user-details`).subscribe({
                next: res => {
                    resolve(res);
                },
                error: () => {
                    resolve(null);
                },
            })
        );
        const preferenceResponse = await new Promise<any>(resolve =>
            this.apiService.get(`${this.resourceUrl}/preferences`).subscribe({
                next: res => {
                    resolve(res);
                },
                error: () => {
                    resolve(null);
                },
            })
        );

        if (preferenceResponse && userResponse) {
            return {
                status: HttpStatusEnum.SUCCESS,
                data: {
                    user: {
                        id: userResponse.userId,
                        firstName: userResponse.firstName,
                        lastName: userResponse.lastName,
                        shortName:
                            userResponse.firstName +
                            ' ' +
                            (userResponse.lastName ? userResponse.lastName[0] + '.' : ''),
                        role: userResponse.isAdmin ? UserRoleEnum.ADMIN : UserRoleEnum.USER,
                        email: userResponse.email,
                        laboratoryId: userResponse.laboratoryId,
                        laboratoryName: userResponse.laboratoryName,
                        organizationId: userResponse.organizationId,
                        organizationName: userResponse.organizationName,
                        country: userResponse.country,
                        registeredAt: new Date(userResponse.registeredAt),
                        canUseSequencing: userResponse.canUseSequencing,
                    },
                    preferences: {
                        userId: userResponse.userId,
                        preferences: [
                            {
                                key: UserPreferenceKeysEnum.SHOW_WELCOME_MODAL,
                                value: preferenceResponse.showWelcomeModal,
                            },
                            {
                                key: UserPreferenceKeysEnum.SHOW_ORDER_SETUP_BY_CSV_GUIDE,
                                value: preferenceResponse.showOrderSetupByCsvGuide,
                            },
                        ],
                    },
                },
            };
        }

        return {
            status: HttpStatusEnum.ERROR,
            data: null,
        };
    }

    getUserGroups(): Promise<HttpResponse<UserGroup[]>> {
        return new Promise<HttpResponse<UserGroup[]>>(resolve => {
            this.apiService.get(`${this.groupsResourceUrl}/menu-list`).subscribe({
                next: (response: MenuListDTO[]) => {
                    resolve({
                        data: response.map(group => {
                            return {
                                id: group.id,
                                name: group.name,
                                isDefault: group.isDefault,
                            };
                        }),
                        status: HttpStatusEnum.SUCCESS,
                    });
                },
                error: () => {
                    resolve({ status: HttpStatusEnum.ERROR, data: null });
                },
            });
        });
    }

    switchGroup(groupId: number): Promise<HttpResponse<void>> {
        return new Promise<HttpResponse<void>>(resolve => {
            this.apiService.post(`${this.groupsResourceUrl}/${groupId}/switch`).subscribe({
                next: (response: any) => {
                    resolve({
                        data: response,
                        status: HttpStatusEnum.SUCCESS,
                    });
                },
                error: () => {
                    ApiHelper.resolveError(resolve, '');
                },
            });
        });
    }

    saveUserPreferences(userPreference: UserPreferencesPayload): Promise<null> {
        return new Promise<null>(resolve => {
            this.apiService.put(`${this.resourceUrl}/preferences`, userPreference).subscribe({
                next: () => {
                    resolve(null);
                },
                error: () => {
                    ApiHelper.resolveError(resolve, '');
                },
            });
        });
    }
}
